import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { loadConfig, onAuthChange, isSignedIn, type PublicConfig } from "./auth";
import { api } from "./api";

export interface Me {
  user: { id: string; email: string; name: string | null; isPlatformAdmin: boolean; mfa: boolean };
  workspaces: Array<{ slug: string; name: string; role: string; plan: string }>;
}

interface Session {
  config: PublicConfig;
  me: Me | null;
  refreshMe: () => Promise<void>;
}

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);

  const refreshMe = async () => {
    if (!isSignedIn()) {
      setMe(null);
      return;
    }
    try {
      setMe(await api.get<Me>("/api/me"));
    } catch {
      setMe(null);
    }
  };

  useEffect(() => {
    void (async () => {
      setConfig(await loadConfig());
      await refreshMe();
      setReady(true);
    })();
    return onAuthChange(() => void refreshMe());
  }, []);

  if (!ready || !config) return <div className="empty">Loading…</div>;
  return <Ctx.Provider value={{ config, me, refreshMe }}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("SessionProvider missing");
  return s;
}

export function langName(config: PublicConfig, code: string): string {
  return config.languages.find((l) => l.code === code)?.name ?? code;
}
